import { TariffSortField, PaginatedTariffsQueryVariables } from "@/graphql/__generated__/graphql";
import { useTranslation } from "react-i18next";
import SortDropdownFilter from "../../Shared/SortDropdownFilter";

interface Props {
  displayAmount: number;
  handleSubmitClick: (_variables: PaginatedTariffsQueryVariables) => void;
}

/**
 * TariffsSortingDropdown component
 * Renders a dropdown menu allowing users to sort tariff data by various fields and sort orders.
 * Integrates with pagination state and triggers a callback with updated query variables when the sort changes.
 *
 * @component
 * @param {Object} props - Component props
 * @param {number} props.displayAmount - The number of tariff items to display per page.
 * @param {(variables: PaginatedTariffsQueryVariables) => void} props.handleSubmitClick - Callback invoked with updated query variables when the sort is changed.
 * @returns {JSX.Element} A dropdown menu for selecting tariff sort field and order.
 *
 * @example
 * <TariffsSortingDropdown
 *   displayAmount={20}
 *   handleSubmitClick={handleTariffSortChange}
 * />
 */
const TariffsSortingDropdown = (props: Props) => {
  const { t } = useTranslation(["translation", "common", "components"]);

  /**
   * Gets user-friendly names for sort fields
   * @param {TariffSortField} field - The sort field to get a display name for
   * @returns {string} User-friendly name for the sort field
   */
  const getSortFieldName = (field: TariffSortField): string => {
    switch (field) {
    case TariffSortField.Province:
      return t("components:filters.tariffs.sortfields.province");
    case TariffSortField.ActiveFrom:
      return t("components:filters.tariffs.sortfields.activefrom");
    default:
      return field;
    }
  };

  const tariffSortConfig = {
    paginationStateKey: "paginatedTariffs" as keyof PaginationState,
    sortFields: TariffSortField,
    getSortFieldName,
    defaultSortField: TariffSortField.Province,
    buildQueryVariables: (displayAmount: number, filters: any, sort: { field: TariffSortField; order: any } | null): PaginatedTariffsQueryVariables => ({
      first: displayAmount,
      filters: {
        province: filters.province,
        activeFrom: filters.activeFrom,
      },
      sort: sort
    }),
    extractFilters: (paginationState: any) => ({
      province: paginationState.filters?.province,
      activeFrom: paginationState.filters?.activeFrom,
    })
  };

  return (
    <SortDropdownFilter
      displayAmount={props.displayAmount}
      handleSubmitClick={props.handleSubmitClick}
      config={tariffSortConfig}
    />
  );
};

export default TariffsSortingDropdown;
